"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"
import type { GetVaultKinkInterestRateModelInfoErrorType } from "../actions/getVaultKinkInterestRateModelInfo"
import {
  type GetVaultKinkInterestRateModelInfoData,
  type GetVaultKinkInterestRateModelInfoOptions,
  type GetVaultKinkInterestRateModelInfoQueryFnData,
  type GetVaultKinkInterestRateModelInfoQueryKey,
  getVaultKinkInterestRateModelInfoQueryOptions,
} from "../query"

import { Evaluate } from "viem"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseVaultKinkInterestRateModelInfoParameters<
  config extends Config = Config,
  selectData = GetVaultKinkInterestRateModelInfoData,
> = Evaluate<
  GetVaultKinkInterestRateModelInfoOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetVaultKinkInterestRateModelInfoQueryFnData,
                GetVaultKinkInterestRateModelInfoErrorType,
                selectData,
                GetVaultKinkInterestRateModelInfoQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (
        data: GetVaultKinkInterestRateModelInfoQueryFnData,
      ) => selectData
    }
>

export type UseVaultKinkInterestRateModelInfoReturnType<
  selectData = GetVaultKinkInterestRateModelInfoData,
> = UseQueryReturnType<selectData, GetVaultKinkInterestRateModelInfoErrorType>

export function useVaultKinkInterestRateModelInfo<
  config extends ExtendedConfig,
  selectData = GetVaultKinkInterestRateModelInfoData,
>(
  parameters: UseVaultKinkInterestRateModelInfoParameters<
    config,
    selectData
  > = {},
): UseVaultKinkInterestRateModelInfoReturnType<selectData> {
  const {
    query = {},
    address,
    evcAddress,
    vaultLensAddress,
    scopeKey,
  } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } =
    getVaultKinkInterestRateModelInfoQueryOptions(config as ExtendedConfig, {
      address,
      chainId: parameters.chainId ?? chainId,
      evcAddress,
      vaultLensAddress,
      scopeKey,
    })
  const enabled = Boolean(address && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    retry: false,
    refetchOnMount: false,
  })
}
